import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink } from "../components/atoms";
import { MediaSlot } from "../components/MediaSlot";
import { MobileActionBar } from "../components/MobileActionBar";
import { SERVICE_FIT, WALKTHROUGHS, REPORT_STRUCTURE, SUBURBIQ_MEDIA, CALENDLY_URL } from "../data/content";
import { api, streamPost } from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Your question", "Your suburb", "Your details", "Payment"];

const DECISION_OPTIONS = [
  { key: "buying", label: "Buying" },
  { key: "selling", label: "Selling" },
  { key: "retaining", label: "Retaining" },
  { key: "comparing", label: "Comparing markets" },
  { key: "other", label: "Something else" },
];

const STATES = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
const TIMEFRAMES = ["Under 2 years", "2–5 years", "5–10 years", "10+ years", "Not sure"];

const initial = {
  decision: "",
  question: "",
  suburb: "",
  state: "",
  postcode: "",
  property_type: "",
  timeframe: "",
  suburb2: "",
  state2: "",
  postcode2: "",
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  extra_notes: "",
  scope: false,
  partner_code: "",
};

const inputCls =
  "w-full bg-transparent border border-charcoal/25 px-4 py-3.5 text-sm text-charcoal placeholder:text-charcoal/40 outline-none transition-colors duration-300 focus:border-gold";
const labelCls = "block text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/60 mb-2";

const Field = ({ label, optional, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>
      {label} {optional && <span className="text-charcoal/35 normal-case tracking-normal">— optional</span>}
    </label>
    {children}
  </div>
);

// Renders genuine approved sample media ONLY when SUBURBIQ_MEDIA is published
// with real, licence-cleared assets (see data/content.js). No placeholders.
const SampleMedia = () => {
  if (SUBURBIQ_MEDIA.status !== "published") return null;
  const { videoUrl, posterUrl, transcriptUrl, sampleImages = [], samplePdfUrl } = SUBURBIQ_MEDIA;
  if (!videoUrl && sampleImages.length === 0 && !samplePdfUrl) return null;
  return (
    <div className="mt-14" data-testid="suburbiq-sample-media">
      <h3 className="text-xs font-semibold tracking-[0.3em] uppercase text-golddark">A genuine sample</h3>
      {videoUrl && (
        <div className="mt-6 max-w-3xl">
          <video controls preload="none" poster={posterUrl || undefined} className="w-full border border-charcoal/15">
            <source src={videoUrl} />
          </video>
          {transcriptUrl && (
            <a href={transcriptUrl} target="_blank" rel="noopener noreferrer" data-testid="sample-transcript-link" className="mt-3 inline-block text-xs font-semibold tracking-[0.2em] uppercase text-charcoal underline underline-offset-4 hover:text-golddark transition-colors duration-300">
              Read the transcript
            </a>
          )}
        </div>
      )}
      {sampleImages.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sampleImages.map((img) => (
            <figure key={img.src} className="border border-charcoal/15 bg-ivory p-4">
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full"
                onError={(e) => e.currentTarget.closest("figure")?.remove()}
              />
              {img.caption && <figcaption className="mt-3 text-xs text-charcoal/60 leading-relaxed">{img.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
      {samplePdfUrl && (
        <a href={samplePdfUrl} target="_blank" rel="noopener noreferrer" data-testid="sample-pdf-link" className="mt-6 inline-block text-xs font-semibold tracking-[0.2em] uppercase text-charcoal underline underline-offset-4 hover:text-golddark transition-colors duration-300">
          View the sample report (PDF)
        </a>
      )}
    </div>
  );
};

export default function SuburbIQ() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [sharpening, setSharpening] = useState(false);
  const [partnerResult, setPartnerResult] = useState(null); // {reference, message} once a complimentary order is reserved
  // Idempotency key for the partner branch: generated once per form session so
  // a double-submit never consumes allowance twice.
  const [requestId] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`));

  // Personal partner links (/suburbiq?code=KeyPartner…) prefill the code field
  // as a convenience ONLY. A prefilled code establishes no identity and grants
  // no entitlement — everything is validated server-side at submission.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) setForm((f) => ({ ...f, partner_code: code.trim() }));
  }, []);

  // Hero "Buy SuburbIQ" starts the existing order flow immediately: jump
  // straight to the order section and move keyboard focus to its first
  // control — no intermediate screen, and the form/payment logic is the
  // existing tested one (nothing duplicated).
  const startOrder = useCallback((e) => {
    const el = document.getElementById("order");
    if (!el) return; // fall back to the #order anchor
    e.preventDefault();
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    window.setTimeout(
      () => el.querySelector("button, input, select, textarea")?.focus({ preventScroll: true }),
      reduced ? 0 : 450
    );
  }, []);

  const sharpen = () => {
    if (sharpening) return;
    if (form.question.trim().length < 12) {
      setError("Write a rough version of your question first — even messy is fine.");
      return;
    }
    setError("");
    setSharpening(true);
    let acc = "";
    streamPost(
      "/ai/sharpen-question",
      { draft: form.question.trim(), decision: form.decision || null },
      {
        onDelta: (t) => {
          acc += t;
          setForm((f) => ({ ...f, question: acc }));
        },
        onError: (err) => toast.error(err),
        onDone: () => setSharpening(false),
      }
    );
  };

  const set = (k) => (e) => {
    const v = e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const validateStep = () => {
    if (step === 0) {
      if (!form.decision) return "Choose what you’re weighing up.";
      if (form.question.trim().length < 12) return "Tell us a little more about the question you’d like answered.";
    }
    if (step === 1) {
      if (!form.suburb.trim() || !form.state || !/^\d{4}$/.test(form.postcode) || !form.property_type)
        return "Suburb, state, postcode (4 digits) and property type are required.";
      if (form.decision === "comparing" && (!form.suburb2.trim() || !form.state2 || !/^\d{4}$/.test(form.postcode2)))
        return "Add the second suburb, state and postcode you’re comparing.";
    }
    if (step === 2) {
      if (!form.first_name.trim() || !form.last_name.trim()) return "Your first and last name are required.";
      if (!validEmail(form.email)) return "Enter a valid email address.";
      if (form.mobile.replace(/\D/g, "").length < 8) return "Enter a valid mobile number.";
      if (!form.scope) return "Please confirm you understand the SuburbIQ scope.";
    }
    return "";
  };

  const next = () => {
    const err = validateStep();
    setError(err);
    if (!err) setStep((s) => Math.min(3, s + 1));
  };
  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const orderPayload = () => ({
    decision: form.decision,
    question: form.question.trim(),
    suburb: form.suburb.trim(),
    state: form.state,
    postcode: form.postcode,
    property_type: form.property_type,
    timeframe: form.timeframe || null,
    suburb2: form.decision === "comparing" ? form.suburb2.trim() : null,
    state2: form.decision === "comparing" ? form.state2 : null,
    postcode2: form.decision === "comparing" ? form.postcode2 : null,
    extra_notes: form.extra_notes.trim() || null,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    mobile: form.mobile.trim(),
    scope_acknowledged: form.scope,
  });

  const pay = async () => {
    setPaying(true);
    try {
      const orderRes = await api.post("/suburbiq/orders", orderPayload());
      const checkout = await api.post("/payments/checkout", {
        lookup_key: "suburbiq_report",
        origin_url: window.location.origin,
        order_id: orderRes.data.order_id,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" ")
        : detail || "Could not start checkout. Your order is saved and you have not been charged — please try again.";
      toast.error(msg);
      setPaying(false);
    }
  };

  // Complimentary partner branch: no checkout is created and no card is
  // taken. The server validates the code, the registered-email match and the
  // remaining allowance, then emails a one-time confirmation link.
  const submitPartnerOrder = async () => {
    setPaying(true);
    setError("");
    try {
      const res = await api.post("/suburbiq/partner-orders", {
        ...orderPayload(),
        partner_code: form.partner_code.trim(),
        request_id: requestId,
        origin_url: window.location.origin,
      });
      setPartnerResult({ reference: res.data.reference, message: res.data.message });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" ")
        : detail || "Could not submit your order. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setPaying(false);
    }
  };

  const hasPartnerCode = form.partner_code.trim().length > 0;

  const decisionLabel = DECISION_OPTIONS.find((d) => d.key === form.decision)?.label;

  return (
    <>
      <Seo
        title="SuburbIQ — Your question. The metrics that answer it. | 80 Metrics"
        description="A professionally reviewed suburb report and video walkthrough from Matthew Clark, focused on the question you want answered. $220 incl. GST, delivered within 2 business days."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "SuburbIQ", path: "/suburbiq" }])]}
      />

      <section className="bg-charcoal text-ivory" data-testid="suburbiq-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-16 lg:pb-24">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Understand · SuburbIQ</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Data is everywhere.</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="italic text-gold">Answers aren’t.</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.8} className="mt-8 max-w-2xl">
            <p className="font-serif text-xl lg:text-2xl text-ivory/90">Your question. The metrics that answer it.</p>
            <p className="mt-4 text-sm lg:text-base leading-relaxed text-ivory/60">
              A professionally reviewed suburb report and video walkthrough from Matthew Clark, focused on the question you want answered.
            </p>
            <p className="mt-6 text-sm text-ivory/80">
              <span className="text-goldlight font-semibold">$220 incl. GST</span>
              <span className="mx-3 text-ivory/30">·</span>
              Delivered within 2 business days
              <span className="mx-3 text-ivory/30">·</span>
              Money-back answer guarantee
            </p>
            <a
              href="#order"
              onClick={startOrder}
              data-testid="suburbiq-hero-cta"
              className="mt-8 inline-flex items-center justify-center px-8 py-4 bg-gold text-ink text-sm font-semibold transition-[background-color] duration-300 hover:bg-ivory"
            >
              Buy SuburbIQ — $220
            </a>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="suburbiq-hero-call"
              className="mt-4 inline-block text-sm font-medium text-ivory/70 underline-offset-4 transition-colors duration-300 hover:text-goldlight"
            >
              Ask a question first — book a 15-minute call →
            </a>
            <p className="mt-8 text-xs leading-relaxed text-ivory/45 max-w-lg">
              Suburb-level research and professional commentary. Not a formal valuation or property-specific recommendation.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-bone border-b border-charcoal/10" data-testid="service-fit-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
              Which service fits your question?
            </h2>
          </Reveal>
          <div className="mt-10 border-t border-charcoal/15">
            {SERVICE_FIT.map((row, i) => (
              <Reveal key={row.service} delay={i * 0.06}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-8 py-7 border-b border-charcoal/15 items-baseline">
                  <p className="lg:col-span-4 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/70 leading-relaxed">
                    {row.question}
                  </p>
                  <p className="lg:col-span-2 text-sm font-semibold text-golddark">{row.service}</p>
                  <p className="lg:col-span-4 text-sm text-charcoal/60 leading-relaxed">{row.when}</p>
                  <p className="lg:col-span-2 lg:text-right text-xs font-semibold tracking-[0.16em] uppercase">
                    {row.action ? (
                      <ArrowLink to={row.action.to} data-testid={`service-fit-${row.service.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {row.action.label}
                      </ArrowLink>
                    ) : (
                      <span className="text-charcoal/40">You’re here now.</span>
                    )}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-8">
            <ArrowLink to="/contact" data-testid="service-fit-fallback">
              Not sure? Tell us what you’re trying to decide
            </ArrowLink>
          </Reveal>
        </div>
      </section>

      <section className="bg-white border-b border-charcoal/10" data-testid="suburbiq-walkthrough">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <Reveal>
            <Eyebrow>The deliverable</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
              Your report comes with Matthew’s walkthrough.
            </h2>
            <p className="mt-6 text-sm lg:text-base text-charcoal/65 max-w-2xl leading-relaxed">
              Every SuburbIQ report is structured the same way, so the answer is easy to use — and easy to challenge.
            </p>
          </Reveal>
          <MediaSlot id="suburbiq-inside" className="mt-12 max-w-3xl" />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/15 border border-charcoal/15">
            {REPORT_STRUCTURE.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.06} className="bg-ivory p-7 lg:p-8">
                <span className="font-serif text-gold text-lg">0{i + 1}</span>
                <h3 className="mt-3 text-xs font-semibold tracking-[0.24em] uppercase text-charcoal" data-testid={`report-part-${r.name.toLowerCase()}`}>{r.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{r.body}</p>
              </Reveal>
            ))}
          </div>
          <ol className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12" data-testid="suburbiq-walkthrough-steps">
            {WALKTHROUGHS.suburbiq.steps.map((st, i) => (
              <li key={st.name}>
                <Reveal delay={i * 0.08}>
                  <span className="font-serif text-3xl text-gold">0{i + 1}</span>
                  <h3 className="mt-3 text-sm font-semibold tracking-[0.14em] uppercase text-charcoal">{st.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{st.body}</p>
                </Reveal>
              </li>
            ))}
          </ol>
          <SampleMedia />
        </div>
      </section>

      <section id="order" className="bg-white" data-testid="suburbiq-order-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <ol className="flex lg:flex-col gap-6 lg:gap-0 lg:sticky lg:top-28 overflow-x-auto">
              {STEPS.map((s, i) => (
                <li
                  key={s}
                  className={`flex items-center gap-4 lg:py-5 lg:border-b lg:border-charcoal/10 ${
                    i === step ? "" : "opacity-45"
                  } transition-opacity duration-300`}
                  data-testid={`step-indicator-${i}`}
                >
                  <span className={`font-serif text-2xl ${i === step ? "text-golddark" : "text-charcoal/50"}`}>
                    0{i + 1}
                  </span>
                  <span className="text-[11px] font-semibold tracking-[0.24em] uppercase whitespace-nowrap">{s}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {step === 0 && (
                  <div data-testid="step-question">
                    <h2 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">What are you weighing up?</h2>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-charcoal/15 border border-charcoal/15">
                      {DECISION_OPTIONS.map((d) => (
                        <button
                          key={d.key}
                          onClick={() => set("decision")(d.key)}
                          data-testid={`decision-${d.key}`}
                          className={`px-4 py-6 text-[11px] font-semibold tracking-[0.2em] uppercase transition-[background-color,color] duration-300 ${
                            form.decision === d.key
                              ? "bg-charcoal text-gold"
                              : "bg-white text-charcoal/70 hover:bg-bone"
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-10">
                      <Field label="What would you like the evidence to help you understand?" htmlFor="question">
                        <textarea
                          id="question"
                          rows={5}
                          value={form.question}
                          onChange={set("question")}
                          data-testid="question-input"
                          placeholder="e.g. Is Reservoir still within reach for a first investment, or should I be looking further out?"
                          className={inputCls}
                        />
                      </Field>
                      <p className="mt-3 text-xs text-charcoal/50 leading-relaxed">
                        Tell us what you’re weighing up. We’ll select the metrics that best answer your question.
                      </p>
                      <button
                        type="button"
                        onClick={sharpen}
                        disabled={sharpening}
                        data-testid="sharpen-button"
                        className="mt-4 inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/70 transition-[color,border-color] duration-300 hover:border-gold hover:text-golddark disabled:opacity-50"
                      >
                        {sharpening ? "Sharpening…" : "Sharpen my question with AI"}
                      </button>
                      {sharpening && (
                        <p className="mt-2 text-xs text-golddark" data-testid="sharpening-note">AI is rewriting your draft — you can keep editing after it finishes.</p>
                      )}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="step-suburb">
                    <h2 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">
                      {form.decision === "comparing" ? "Your first suburb" : "Your suburb"}
                    </h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Field label="Suburb" htmlFor="suburb">
                        <input id="suburb" value={form.suburb} onChange={set("suburb")} data-testid="suburb-input" className={inputCls} placeholder="e.g. Reservoir" />
                      </Field>
                      <Field label="State" htmlFor="state">
                        <select id="state" value={form.state} onChange={set("state")} data-testid="state-select" className={inputCls}>
                          <option value="">Select state</option>
                          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </Field>
                      <Field label="Postcode" htmlFor="postcode">
                        <input id="postcode" value={form.postcode} onChange={set("postcode")} data-testid="postcode-input" maxLength={4} inputMode="numeric" className={inputCls} placeholder="3073" />
                      </Field>
                      <Field label="Property type" htmlFor="property_type">
                        <select id="property_type" value={form.property_type} onChange={set("property_type")} data-testid="property-type-select" className={inputCls}>
                          <option value="">House or unit</option>
                          <option value="house">House</option>
                          <option value="unit">Unit</option>
                        </select>
                      </Field>
                      <Field label="Timeframe" optional htmlFor="timeframe">
                        <select id="timeframe" value={form.timeframe} onChange={set("timeframe")} data-testid="timeframe-select" className={inputCls}>
                          <option value="">Select timeframe</option>
                          {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                    </div>

                    {form.decision === "comparing" && (
                      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-12" data-testid="second-suburb-block">
                        <h3 className="font-serif text-2xl font-medium text-charcoal">The suburb you’re comparing it with</h3>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                          <Field label="Suburb" htmlFor="suburb2">
                            <input id="suburb2" value={form.suburb2} onChange={set("suburb2")} data-testid="suburb2-input" className={inputCls} placeholder="e.g. Thomastown" />
                          </Field>
                          <Field label="State" htmlFor="state2">
                            <select id="state2" value={form.state2} onChange={set("state2")} data-testid="state2-select" className={inputCls}>
                              <option value="">Select</option>
                              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </Field>
                          <Field label="Postcode" htmlFor="postcode2">
                            <input id="postcode2" value={form.postcode2} onChange={set("postcode2")} data-testid="postcode2-input" maxLength={4} inputMode="numeric" className={inputCls} placeholder="3074" />
                          </Field>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="step-details">
                    <h2 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">Your details</h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Field label="First name" htmlFor="first_name">
                        <input id="first_name" value={form.first_name} onChange={set("first_name")} data-testid="first-name-input" className={inputCls} />
                      </Field>
                      <Field label="Last name" htmlFor="last_name">
                        <input id="last_name" value={form.last_name} onChange={set("last_name")} data-testid="last-name-input" className={inputCls} />
                      </Field>
                      <Field label="Email" htmlFor="email">
                        <input id="email" type="email" value={form.email} onChange={set("email")} data-testid="email-input" className={inputCls} />
                      </Field>
                      <Field label="Mobile" htmlFor="mobile">
                        <input id="mobile" type="tel" value={form.mobile} onChange={set("mobile")} data-testid="mobile-input" className={inputCls} placeholder="04xx xxx xxx" />
                      </Field>
                    </div>
                    <div className="mt-8">
                      <Field label="Anything else that would help us understand your question?" optional htmlFor="extra_notes">
                        <textarea id="extra_notes" rows={4} value={form.extra_notes} onChange={set("extra_notes")} data-testid="extra-notes-input" className={inputCls} />
                      </Field>
                    </div>
                    <div className="mt-8">
                      <Field label="Partner code" optional htmlFor="partner_code">
                        <input
                          id="partner_code"
                          value={form.partner_code}
                          onChange={set("partner_code")}
                          data-testid="partner-code-input"
                          className={inputCls}
                          placeholder="e.g. KeyPartner…"
                          autoComplete="off"
                        />
                      </Field>
                      <p className="mt-2 text-xs text-charcoal/55 leading-relaxed" data-testid="partner-code-hint">
                        Only if a partner gave you one. The code must match your registered email — you'll confirm by email before anything is reserved. Leave blank to pay normally.
                      </p>
                    </div>
                    <label className="mt-8 flex items-start gap-4 cursor-pointer border border-charcoal/15 p-5" data-testid="scope-checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.scope}
                        onChange={set("scope")}
                        data-testid="scope-checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
                      />
                      <span className="text-xs leading-relaxed text-charcoal/70">
                        I understand SuburbIQ provides suburb-level research and professional commentary, not personalised advice about a specific property.
                      </span>
                    </label>
                  </div>
                )}

                {step === 3 && partnerResult && (
                  <div data-testid="partner-pending-panel">
                    <h2 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">One last step — check your email</h2>
                    <div className="mt-8 bg-charcoal text-ivory p-8 lg:p-10">
                      <p className="font-serif text-2xl text-gold" data-testid="partner-order-reference">{partnerResult.reference}</p>
                      <p className="mt-4 text-sm text-ivory/75 leading-relaxed">
                        {partnerResult.message || "We've emailed a confirmation link to your registered email address."}
                      </p>
                      <p className="mt-3 text-sm text-ivory/75 leading-relaxed">
                        No payment is taken for an approved partner order. Matthew prepares each report personally — nothing is automated — and your report follows within 2 business days of your confirmation.
                      </p>
                    </div>
                  </div>
                )}

                {step === 3 && !partnerResult && (
                  <div data-testid="step-payment">
                    <h2 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">Confirm your SuburbIQ</h2>
                    <dl className="mt-8 border-t border-charcoal/15">
                      {[
                        ["Your decision", decisionLabel],
                        ["Your question", form.question],
                        [
                          "Suburb",
                          form.decision === "comparing"
                            ? `${form.suburb} ${form.state} ${form.postcode} vs ${form.suburb2} ${form.state2} ${form.postcode2}`
                            : `${form.suburb} ${form.state} ${form.postcode}`,
                        ],
                        ["Property type", form.property_type === "house" ? "House" : "Unit"],
                        ["Timeframe", form.timeframe || "—"],
                      ].map(([k, v]) => (
                        <div key={k} className="grid grid-cols-3 gap-4 py-4 border-b border-charcoal/10">
                          <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-charcoal/50 pt-1">{k}</dt>
                          <dd className="col-span-2 text-sm text-charcoal leading-relaxed">{v}</dd>
                        </div>
                      ))}
                    </dl>

                    <div className="mt-10 bg-charcoal text-ivory p-8 lg:p-10" data-testid="order-summary-card">
                      <div className="flex flex-wrap items-baseline justify-between gap-4">
                        <h3 className="font-serif text-2xl lg:text-3xl font-medium">SuburbIQ</h3>
                        {hasPartnerCode ? (
                          <p className="font-serif text-2xl lg:text-3xl text-gold" data-testid="order-price">$0 <span className="text-sm text-ivory/60">complimentary partner report — after email confirmation</span></p>
                        ) : (
                          <p className="font-serif text-2xl lg:text-3xl text-gold" data-testid="order-price">$220 <span className="text-sm text-ivory/60">including GST</span></p>
                        )}
                      </div>
                      <ul className="mt-6 space-y-2.5 text-sm text-ivory/70">
                        <li>Professionally reviewed suburb analysis</li>
                        <li>Matthew’s video walkthrough</li>
                        <li>{hasPartnerCode ? "Delivered within 2 business days of your email confirmation" : "Delivered within 2 business days of payment"}</li>
                        <li>Money-back answer guarantee</li>
                      </ul>
                      {hasPartnerCode ? (
                        <>
                          <p className="mt-6 text-xs text-ivory/60 leading-relaxed" data-testid="partner-confirm-note">
                            Partner code <span className="font-semibold text-ivory">{form.partner_code.trim()}</span> — if it matches your registered email and your allowance is available, no payment is taken. We’ll email you a one-time confirmation link.
                          </p>
                          <button
                            onClick={submitPartnerOrder}
                            disabled={paying}
                            data-testid="partner-submit-button"
                            className="mt-6 w-full px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory disabled:opacity-60"
                          >
                            {paying ? "Submitting…" : "Submit with partner code"}
                          </button>
                          <button
                            onClick={() => setForm((f) => ({ ...f, partner_code: "" }))}
                            data-testid="partner-remove-code"
                            className="mt-4 w-full text-xs text-ivory/50 underline underline-offset-4 hover:text-gold transition-colors duration-300"
                          >
                            Remove the code and pay $220 instead
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={pay}
                          disabled={paying}
                          data-testid="pay-button"
                          className="mt-8 w-full px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory disabled:opacity-60"
                        >
                          {paying ? "Preparing secure checkout…" : "Pay $220 securely"}
                        </button>
                      )}
                      {!hasPartnerCode && (
                        <p className="mt-4 text-xs text-ivory/45">Payment is processed securely. We do not store your card details.</p>
                      )}
                      <p className="mt-2 text-xs text-ivory/45">
                        Your details are handled as described in our{" "}
                        <Link to="/privacy-policy" data-testid="checkout-privacy-link" className="underline underline-offset-4 hover:text-gold transition-colors duration-300">
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                )}

                {error && (
                  <p className="mt-6 text-sm text-red-700" data-testid="step-error">{error}</p>
                )}

                <div className="mt-10 flex items-center justify-between gap-4">
                  {step > 0 ? (
                    <button onClick={back} data-testid="step-back" className="text-xs font-semibold tracking-[0.2em] uppercase text-charcoal/60 hover:text-charcoal transition-colors duration-300">
                      ← Back
                    </button>
                  ) : <span />}
                  {step < 3 && (
                    <button
                      onClick={next}
                      data-testid="step-next"
                      className="px-8 py-4 bg-charcoal text-ivory text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink"
                    >
                      Continue
                    </button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <MobileActionBar serviceKey="suburbiq" watchTestId="suburbiq-hero-cta" />
    </>
  );
}
